import { Stack, Typography } from '@mui/material';
import { EvaluationType, useGetAreasOfLearningQuery } from '@schooly/api';
import { TagSelect } from '@schooly/style';
import { FC, useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DropdownSelect, DropdownSelectProps } from '../DropdownSelect';
import { DropdownSelectRow } from '../DropdownSelectRow';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectSearchInput } from '../SelectSearchInput';
import { renderAreaOfLearningTags } from './EvaluationTypeExpandedSelect';

type EvaluationTypeSelectProps = {
  schoolId: string;
  selectedEvaluationTypes: EvaluationType[];
  selectedAreaOfLearningIds: string[];
  onSelectEvaluationType: (type: EvaluationType) => void;
  onSelectAreaOfLearning: (id: string) => void;
} & Omit<DropdownSelectProps, 'children' | 'renderContent'>;

export const EvaluationTypeSelectMultiple: FC<EvaluationTypeSelectProps> = ({
  schoolId,
  placeholder,
  selectedEvaluationTypes,
  selectedAreaOfLearningIds,
  onSelectEvaluationType,
  onSelectAreaOfLearning,
  ...dropdownProps
}) => {
  const { $t } = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');

  const { data } = useGetAreasOfLearningQuery(schoolId, {
    enabled: !!schoolId,
    refetchOnMount: 'always',
  });

  const tutorFeedbackLabel = $t({ id: 'assessments-TutorsFeedback' });
  const isTutorFeedbackSelected = selectedEvaluationTypes.includes(EvaluationType.TutorFeedback);

  const handleSelectAreaOfLearning = useCallback(
    (id: string) => {
      const isAreaOfLearningTypeSelected = selectedEvaluationTypes.includes(
        EvaluationType.AreaOfLearning,
      );
      const isSelectedId = selectedAreaOfLearningIds.includes(id);
      const shouldSelectAreaOfLearningType = !isSelectedId && !isAreaOfLearningTypeSelected;
      const shouldDeselectAreaOfLearningType =
        isSelectedId && selectedAreaOfLearningIds.length === 1 && isAreaOfLearningTypeSelected;

      if (shouldSelectAreaOfLearningType || shouldDeselectAreaOfLearningType) {
        onSelectEvaluationType(EvaluationType.AreaOfLearning);
      }

      onSelectAreaOfLearning(id);
    },
    [
      onSelectAreaOfLearning,
      onSelectEvaluationType,
      selectedAreaOfLearningIds,
      selectedEvaluationTypes,
    ],
  );

  const renderContent = useCallback(() => {
    if (!data) return <SelectContentSkeleton />;

    const areasOfLearning = data.filter((a) => a.name.toLowerCase().includes(query.toLowerCase()));
    const showTutorFeedback = tutorFeedbackLabel.toLowerCase().includes(query.toLowerCase());

    if (!areasOfLearning.length && !showTutorFeedback) {
      return (
        <Typography variant="h3" p={1} color="common.grey2">
          <FormattedMessage id="input-NoOptionsFound" />
        </Typography>
      );
    }

    return (
      <Stack gap={0.5} m={1}>
        {showTutorFeedback && (
          <DropdownSelectRow
            key={tutorFeedbackLabel}
            isSelected={isTutorFeedbackSelected}
            onClick={() => onSelectEvaluationType(EvaluationType.TutorFeedback)}
          >
            {tutorFeedbackLabel}
          </DropdownSelectRow>
        )}

        {areasOfLearning.map((a) => {
          const isSelected = selectedAreaOfLearningIds.includes(a.id);

          return (
            <DropdownSelectRow
              key={a.id}
              isSelected={isSelected}
              onClick={() => handleSelectAreaOfLearning(a.id)}
            >
              {a.name}
            </DropdownSelectRow>
          );
        })}
      </Stack>
    );
  }, [
    data,
    handleSelectAreaOfLearning,
    isTutorFeedbackSelected,
    onSelectEvaluationType,
    query,
    selectedAreaOfLearningIds,
    tutorFeedbackLabel,
  ]);

  return (
    <DropdownSelect
      {...dropdownProps}
      placeholder={placeholder}
      hasValues={isTutorFeedbackSelected || selectedAreaOfLearningIds.length > 0}
      renderContent={renderContent}
    >
      {(opened) => (
        <>
          {isTutorFeedbackSelected && (
            <TagSelect
              key={tutorFeedbackLabel}
              label={tutorFeedbackLabel}
              size="small"
              sx={{ maxWidth: 200 }}
              onDelete={
                opened ? () => onSelectEvaluationType(EvaluationType.TutorFeedback) : undefined
              }
            />
          )}

          {renderAreaOfLearningTags({
            ids: selectedAreaOfLearningIds,
            onDelete: opened ? handleSelectAreaOfLearning : undefined,
            tagProps: { schoolId, size: 'small', sx: { maxWidth: 200 } },
          })}

          {opened && (
            <SelectSearchInput ref={inputRef} autoFocus value={query} onChangeText={setQuery} />
          )}
        </>
      )}
    </DropdownSelect>
  );
};
